/**
 * The new-experiment form's state. Local to the panel (it is a draft of one form,
 * not tab-wide state), and every default is *derived* from server data during
 * render rather than copied into state, so a list that loads late still fills in.
 */

import { useState } from "react";
import type { Dataset } from "@/features/datasets/types";
import type { Evaluator } from "@/features/evaluators/types";
import type { ExperimentSummary, InputMapping } from "../types";
import type { FormDraft, Runner } from "./wizard-draft";

/** A draft's credential refs as `{key: id}`, whichever shape the API sent. */
function refIds(draft: ExperimentSummary | undefined): Record<string, string> {
  const refs = draft?.credential_refs ?? {};
  return Object.fromEntries(Object.entries(refs).map(([k, r]) => [k, typeof r === "string" ? r : r.id]));
}

/** First-open default: every compatible free evaluator plus up to three judges.
 *  Mirrors the server's own default for `evaluator_ids`, so what is ticked is what runs. */
export function defaultSelection(evaluators: Evaluator[]): Set<string> {
  const ok = evaluators.filter((e) => e.compatible);
  const free = ok.filter((e) => !e.is_paid);
  const paid = ok.filter((e) => e.is_paid).slice(0, 3);
  return new Set([...free, ...paid].map((e) => e.id));
}

/** The dataset the agent's most recent experiment used (`recent` is newest
 *  first), if it still has items; otherwise none — the person picks. */
export function defaultDataset(
  datasets: Dataset[],
  recent: Array<{ dataset: { id: string } | null }>,
): string | null {
  const last = recent[0]?.dataset?.id;
  return datasets.find((d) => d.id === last && (d.latest_version?.item_count ?? 0) > 0)?.id ?? null;
}

/** `saved` (an unsaved draft kept in the tab) wins over the saved experiment `draft`. */
export function useExperimentForm(
  draft: ExperimentSummary | undefined,
  maxCost: number,
  saved: Partial<FormDraft> = {},
) {
  const [name, setName] = useState(saved.name ?? draft?.name ?? "");
  const [description, setDescription] = useState(saved.description ?? draft?.description ?? "");
  const [datasetId, setDatasetId] = useState<string | null>(saved.datasetId ?? draft?.dataset?.id ?? null);
  // null until picked: the dataset decides (a live one only offers its outputs).
  const [runner, setRunner] = useState<Runner | null>(saved.runner ?? (draft ? "agent" : null));
  const [picked, setPicked] = useState<Set<string> | null>(() => {
    const ids = saved.picked !== undefined ? saved.picked : draft?.evaluators.map((e) => e.id);
    return ids ? new Set(ids) : null;
  });
  const [repetitions, setRepetitions] = useState(saved.repetitions ?? draft?.repetitions ?? 1);
  const [costLimit, setCostLimit] = useState(
    saved.costLimit ?? (draft?.spend.limit_usd ?? Math.min(10, maxCost)).toFixed(2),
  );
  // Overrides only: a key absent here shows the spec's default, derived on render.
  const [runSettings, setRunSettings] = useState<Record<string, string>>(
    saved.runSettings ?? { ...draft?.run_settings },
  );
  const [credentialRefs, setCredentialRefs] = useState<Record<string, string>>(
    () => saved.credentialRefs ?? refIds(draft),
  );
  // null until the user changes it (then the last experiment's, if any, shows).
  const [inputMapping, setInputMapping] = useState<InputMapping | null>(
    saved.inputMapping !== undefined ? saved.inputMapping : (draft?.input_mapping ?? null),
  );
  const snapshot: FormDraft = {
    name,
    description,
    datasetId,
    runner,
    picked: picked ? [...picked].sort() : null,
    repetitions,
    costLimit,
    runSettings,
    credentialRefs,
    inputMapping,
  };

  return {
    name,
    setName,
    description,
    setDescription,
    datasetId,
    /** Changing the dataset keeps the picks; incompatible ones drop out on render. */
    setDatasetId,
    runner,
    setRunner,
    picked,
    toggle: (id: string, current: Set<string>) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setPicked(next);
    },
    repetitions,
    setRepetitions,
    costLimit,
    setCostLimit,
    runSettings,
    setRunSetting: (key: string, value: string) => setRunSettings((v) => ({ ...v, [key]: value })),
    credentialRefs,
    setCredentialRef: (key: string, id: string) => setCredentialRefs((v) => ({ ...v, [key]: id })),
    /** The input no longer reads a credential (it reads the dataset or a fixed value). */
    clearCredentialRef: (key: string) => setCredentialRefs(({ [key]: _dropped, ...rest }) => rest),
    inputMapping,
    setInputMapping,
    /** What to keep in the tab, so a reload comes back to it. */
    snapshot,
  };
}

export function costError(value: string, max: number): string | null {
  const n = Number(value);
  if (!value.trim() || Number.isNaN(n) || n <= 0) return "Enter a limit above $0.";
  if (n > max) return `Your limit is $${max.toFixed(2)} per experiment.`;
  return null;
}
